import { FieldSet } from "@prismicio/editor-ui"
import type { CompositeSliceContent } from "@prismicio/types-internal/lib/content"
import type { CompositeSlice } from "@prismicio/types-internal/lib/customtypes"
import type { ReactNode } from "react"

import { getSliceSelectionDOMId, useSliceSelection } from "../SliceSelection"
import { NonRepeatableZoneEditor } from "../zones/NonRepeatableZoneEditor"
import { RepeatableZoneEditor } from "../zones/RepeatableZoneEditor"

interface CompositeSliceEditorProps {
  id: string
  readOnly: boolean
  compositeSlice: CompositeSlice
  content: CompositeSliceContent
  onContentChange: (content: CompositeSliceContent) => void
  sliceKey?: string
  header?: ReactNode
}

export function CompositeSliceEditor(props: CompositeSliceEditorProps) {
  const {
    compositeSlice,
    content,
    onContentChange,
    sliceKey,
    id,
    readOnly,
    header,
  } = props

  const selection = useSliceSelection()

  function onFocus() {
    if (!sliceKey) return
    selection.update({ id: sliceKey, requestedFrom: "sliceFieldSet" })
  }

  return (
    <FieldSet
      id={sliceKey && getSliceSelectionDOMId("sliceFieldSet", sliceKey)}
      selected={sliceKey ? selection.isSelected(sliceKey) : false}
      onFocus={onFocus}
      legend={getLegend(compositeSlice)}
      header={header}
    >
      <NonRepeatableZoneEditor
        id={id}
        readOnly={readOnly}
        nonRepeatableZone={compositeSlice["non-repeat"] ?? {}}
        content={content.nonRepeat}
        onContentChange={(nonRepeatableZoneContent) => {
          onContentChange({ ...content, nonRepeat: nonRepeatableZoneContent })
        }}
      />
      <RepeatableZoneEditor
        id={id}
        readOnly={readOnly}
        repeatableZone={compositeSlice.repeat ?? {}}
        content={content.repeat}
        onContentChange={(repeatableZoneContent) => {
          onContentChange({ ...content, repeat: repeatableZoneContent })
        }}
      />
    </FieldSet>
  )
}

function getLegend(compositeSlice: CompositeSlice): string {
  return (
    compositeSlice.config?.label ??
    compositeSlice.fieldset ??
    compositeSlice.type
  )
}
